import React, { useContext, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  PageContent,
  PageSection,
  PageHeader,
  ControlContainer,
  ControlContainerRow,
} from 'common/styles/page';

import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import { useSelector } from 'react-redux';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import ToggleControl from '../../../common/components/ToggleChartControl';
import AnalyticsDemographicsPie from './AnalyticsDemographicsPie';
import AnalyticsDemographicsBar from './AnalyticsDemographicsBar';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const AnalyticsDemographics = () => {
  const [chartType, setChartType] = useState('pie');

  const organizationId = useSelector(getCurrentOrganizationId);
  const { setTopBarContent } = useContext(TopBarContext);
  const { setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  useEffect(() => {
    setPageInfoTitle('About Demographic Summary');
    setPageInfoIntro(
      'This page shows how the organization’s assessment population is distributed across demographic groups, helping leaders understand response patterns and identify areas where engagement or equity may need more focus.',
    );
    setPageInfoItems([
      {
        term: 'Group comparisons',
        description: 'See which demographic groups are scoring highest and lowest.',
      },
      {
        term: 'Participation trends',
        description: 'Understand whether all groups are represented fairly in the assessment data.',
      },
      {
        term: 'Equity focus',
        description: 'Turn demographic insights into actions that support inclusion and fairness.',
      },
    ]);

    return () => {
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPageInfoItems, setPageInfoTitle, setPageInfoIntro]);

  return (
    <PageContent>
      <PageSection padright>
        <PageHeader
          title="Demographic Summary"
          caption="Assessment response population by demographic groups."
          controls={
            <ControlContainerRow>
              <ControlContainer justify="left">
                <ToggleControl
                  buttonLabels={[
                    { type: 'pie', icon: 'chart pie' },
                    { type: 'bar', icon: 'chart bar' },
                  ]}
                  setChartType={setChartType}
                />
              </ControlContainer>
            </ControlContainerRow>
          }
        />
      </PageSection>
      {chartType === 'pie' ? (
        <AnalyticsDemographicsPie />
      ) : chartType === 'bar' ? (
        <AnalyticsDemographicsBar />
      ) : null}
    </PageContent>
  );
};

export default AnalyticsDemographics;
